import { arrowLeft, arrowRight } from "../../assets/Icons/icons";
import { strings } from "../../strings/strings";

const Pagination = ({
  pagination,
  onPageChange,
  currentItemsCount,

  showInfo = true,
  showPageNumbers = true,
  className = "",
}) => {
  if (!pagination) return null;

  const {
    currentPage = 1,
    totalPages = 1,
    totalItems = 0,
    itemsPerPage = 10,
    hasNextPage = false,
    hasPrevPage = false,
  } = pagination;

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        endPage = Math.min(5, totalPages);
      } else if (currentPage >= totalPages - 2) {
        startPage = Math.max(1, totalPages - 4);
      }

      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push("ellipsis-start");
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push("ellipsis-end");
        }
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      onPageChange(newPage);
    }
  };

  if (totalPages <= 1 && !showInfo) {
    return null;
  }

  return (
    <div className={`flex items-center justify-between gap-4 ${className}`}>
      {showInfo && (
        <div className="text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
          <span className="font-medium text-gray-700">{strings.Showing}</span>{" "}
          <span className="font-bold text-secondary">{startItem}</span>{" "}
          <span className="font-medium text-gray-700">{strings.to}</span>{" "}
          <span className="font-bold text-secondary">{endItem}</span>{" "}
          <span className="font-medium text-gray-700">{strings.of}</span>{" "}
          <span className="font-bold text-secondary">{totalItems}</span>{" "}
        </div>
      )}

      {(totalPages > 1 || showPageNumbers) && (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!hasPrevPage || currentPage === 1}
            className={`
              min-w-[40px] h-10 px-3 rounded-lg 
              flex items-center justify-center
              transition-all duration-200 bg-grey
              border border-gray-300
              ${
                !hasPrevPage || currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                  : "bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm active:bg-gray-100"
              }
            `}
            title="Previous"
          >
            <img src={arrowLeft} alt="Previous" className="w-[18px] h-[18px]" />
          </button>

          {showPageNumbers && totalPages > 1 && (
            <div className="flex items-center gap-1">
              {getPageNumbers().map((page, index) => {
                if (page === "ellipsis-start" || page === "ellipsis-end") {
                  return (
                    <span key={`ellipsis-${index}`} className="px-2 py-2 text-sm font-medium text-gray-400">
                      ...
                    </span>
                  );
                }

                const isActive = page === currentPage;

                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`
                      min-w-[40px] h-10 px-3
                      text-sm font-semibold
                      rounded-lg
                      transition-all duration-200
                      ${
                        isActive
                          ? "bg-grey text-gray-400 cursor-not-allowed border border-gray-200"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm active:bg-gray-100"
                      }
                    `}
                    disabled={isActive}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
          )}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!hasNextPage || currentPage >= totalPages}
            className={`
              min-w-[40px] h-10 px-3 rounded-lg 
              flex items-center justify-center
              transition-all duration-200
              border border-gray-300
              ${
                !hasNextPage || currentPage >= totalPages
                  ? "bg-grey text-gray-400 cursor-not-allowed border-gray-200"
                  : "bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm active:bg-gray-100"
              }
            `}
            title="Next"
          >
            <img src={arrowRight} alt="Next" className="w-[18px] h-[18px]" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
